import { useState, useEffect, useCallback, useRef } from 'react'
import { GridSortModel, GridPaginationModel } from '@mui/x-data-grid'

export interface UseServerTableStateOptions {
  initialPageSize?: number
  initialSortModel?: GridSortModel
  searchDebounceMs?: number
}

export function useServerTableState(options: UseServerTableStateOptions = {}) {
  const {
    initialPageSize = 10,
    initialSortModel = [],
    searchDebounceMs = 300,
  } = options

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: initialPageSize,
  })
  const [sortModel, setSortModelState] = useState<GridSortModel>(initialSortModel)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [dynamicFilters, setDynamicFiltersState] = useState<Record<string, string[]>>({})

  // Setting sort model always resets pagination to page 0
  const setSortModel = useCallback((updater: React.SetStateAction<GridSortModel>) => {
    setSortModelState(updater)
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }))
  }, [])

  // Debounce search input: skip on initial mount since searchInput already matches searchQuery
  const isFirstMountRef = useRef(true)
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false
      return
    }
    const timer = setTimeout(() => {
      setSearchQuery(searchInput)
      setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }))
    }, searchDebounceMs)

    return () => clearTimeout(timer)
  }, [searchInput, searchDebounceMs])

  // Setting dynamic filters always resets pagination to page 0
  const setDynamicFilters = useCallback(
    (updater: React.SetStateAction<Record<string, string[]>>) => {
      setDynamicFiltersState(updater)
      setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }))
    },
    []
  )

  const toggleFilterOption = useCallback(
    (column: string, val: string) => {
      setDynamicFilters((prev) => {
        const current = prev[column] || []
        const next = current.includes(val)
          ? current.filter((s) => s !== val)
          : [...current, val]
        return { ...prev, [column]: next }
      })
    },
    [setDynamicFilters]
  )

  const clearColumnFilter = useCallback(
    (column: string) => {
      setDynamicFilters((prev) => ({ ...prev, [column]: [] }))
    },
    [setDynamicFilters]
  )

  const clearAllFilters = useCallback(() => {
    setSearchInput('')
    setSearchQuery('')
    setDynamicFilters({})
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }))
  }, [setDynamicFilters])

  const sortBy = sortModel[0]?.field
  const sortOrder = (sortModel[0]?.sort as 'asc' | 'desc') || 'desc'
  const isDynamicFiltered = Object.values(dynamicFilters).some((arr) => arr.length > 0)
  const isFiltered = Boolean(searchQuery) || isDynamicFiltered
  const activeFilterCount =
    Object.values(dynamicFilters).filter((arr) => arr.length > 0).length +
    (sortModel.length > 0 ? 1 : 0)

  return {
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    searchInput,
    setSearchInput,
    searchQuery,
    dynamicFilters,
    setDynamicFilters,
    toggleFilterOption,
    clearColumnFilter,
    clearAllFilters,
    sortBy,
    sortOrder,
    isDynamicFiltered,
    isFiltered,
    activeFilterCount,
  }
}

export default useServerTableState
